import { styled } from '@linaria/react';

import Demo, {
  DemoSliderControl,
  DemoRadioControl,
  DemoSelectControl,
} from '@/components/Demo';
import DemoOutputWrapper from '@/components/Demo/OutputWrapper';

import Box from './Box';
import ScaleDemo from './ScaleDemo';
import TranslateDemo from './TranslateDemo';
import TranslateCalcDemo from './TranslateCalcDemo';
import RotateDemo from './RotateDemo';
import SkewDemo from './SkewDemo';
import MultipleDemo from './MultipleDemo';
import OriginDemo from './OriginDemo';
import MultipleOriginDemo from './MultipleOriginDemo';
import PerspectiveDemo from './PerspectiveDemo';
import EnablePerspectiveDemo from './EnablePerspectiveDemo';
import TVTurnOffDemo from './TVTurnOffDemo';

function TransformDemo({ variant }) {
  switch (variant) {
    case 'translate-px':
    case 'translate-px-flow': {
      return (
        <Demo
          id={variant}
          outputWrapper={OutputWrapper}
          initialValues={{
            x: 0,
            y: 0,
          }}
          controls={[
            <DemoSliderControl
              key="x"
              id="x"
              label="x"
              min={-50}
              max={50}
              previewRenderer={(val) => `${val}px`}
            />,
            <DemoSliderControl
              key="y"
              id="y"
              label="y"
              min={-50}
              max={50}
              previewRenderer={(val) => `${val}px`}
            />,
          ]}
        >
          {({ x, y }) => {
            return <TranslateDemo variant={variant} x={x} y={y} />;
          }}
        </Demo>
      );
    }

    case 'translate-percentage': {
      return (
        <Demo
          id={variant}
          outputWrapper={OutputWrapper}
          initialValues={{
            x: 0,
            y: 0,
          }}
          controls={[
            <DemoSliderControl
              key="x"
              id="x"
              label="x"
              min={-100}
              max={100}
              previewRenderer={(val) => `${val}%`}
            />,
            <DemoSliderControl
              key="y"
              id="y"
              label="y"
              min={-100}
              max={100}
              previewRenderer={(val) => `${val}%`}
            />,
          ]}
        >
          {({ x, y }) => {
            return <TranslateDemo variant={variant} x={x} y={y} />;
          }}
        </Demo>
      );
    }

    case 'translate-calc': {
      return (
        <Demo
          id={variant}
          outputWrapper={OutputWrapper}
          initialValues={{
            percentage: 0,
            pixels: 0,
          }}
          controls={[
            <DemoSliderControl
              key="percentage"
              id="percentage"
              label="Percentage"
              min={-100}
              max={100}
              previewRenderer={(val) => `${val}%`}
            />,
            <DemoSliderControl
              key="pixels"
              id="pixels"
              label="Pixels"
              min={-50}
              max={50}
              previewRenderer={(val) => `${val}px`}
            />,
          ]}
        >
          {({ percentage, pixels }) => {
            return (
              <TranslateCalcDemo
                variant={variant}
                percentage={percentage}
                pixels={pixels}
              />
            );
          }}
        </Demo>
      );
    }

    case 'tv-turn-off': {
      return (
        <Demo
          id={variant}
          outputWrapper={OutputWrapper}
          initialValues={{
            isOn: true,
          }}
          controls={[
            <DemoRadioControl
              key="isOn"
              id="isOn"
              label="Power status"
              options={[
                { label: 'On', value: true },
                { label: 'Off', value: false },
              ]}
            />,
          ]}
        >
          {({ isOn }) => {
            return <TVTurnOffDemo isOn={isOn} />;
          }}
        </Demo>
      );
    }

    case 'scale':
    case 'scale-text': {
      return (
        <Demo
          id={variant}
          outputWrapper={OutputWrapper}
          initialValues={{
            ratio: 1,
          }}
          controls={[
            <DemoSliderControl
              key="ratio"
              id="ratio"
              label="Ratio"
              min={0}
              max={2}
              step={0.05}
            />,
          ]}
        >
          {({ ratio }) => {
            return (
              <ScaleDemo
                singleAxis
                withText={variant === 'scale-text'}
                x={ratio}
              />
            );
          }}
        </Demo>
      );
    }
    case 'scale-both': {
      return (
        <Demo
          id={variant}
          outputWrapper={OutputWrapper}
          initialValues={{
            x: 1,
            y: 1,
          }}
          controls={[
            <DemoSliderControl
              key="x"
              id="x"
              label="x"
              min={0}
              max={2}
              step={0.05}
            />,
            <DemoSliderControl
              key="y"
              id="y"
              label="y"
              min={0}
              max={2}
              step={0.05}
            />,
          ]}
        >
          {({ x, y }) => {
            return <ScaleDemo x={x} y={y} />;
          }}
        </Demo>
      );
    }

    case 'rotate-deg':
    case 'rotate-turn': {
      const unit = variant === 'rotate-deg' ? 'deg' : 'turn';
      return (
        <Demo
          id={variant}
          outputWrapper={OutputWrapper}
          initialValues={{
            rotation: 0,
          }}
          controls={[
            <DemoSliderControl
              key="rotation"
              id="rotation"
              label="Rotation"
              min={unit === 'deg' ? -360 : -1}
              max={unit === 'deg' ? 540 : 1}
              step={unit === 'turn' ? 0.1 : 1}
              previewRenderer={(val) => `${val}${unit}`}
            />,
          ]}
        >
          {({ rotation }) => {
            return <RotateDemo rotation={rotation} unit={unit} />;
          }}
        </Demo>
      );
    }

    case 'skew': {
      return (
        <Demo
          id={variant}
          outputWrapper={OutputWrapper}
          initialValues={{
            rotation: 0,
          }}
          controls={[
            <DemoSliderControl
              key="rotation"
              id="rotation"
              label="Skew"
              min={-45}
              max={45}
              previewRenderer={(val) => `${val}deg`}
            />,
          ]}
        >
          {({ rotation }) => {
            return <SkewDemo variant={variant} rotation={rotation} />;
          }}
        </Demo>
      );
    }
    case 'skew-axes': {
      return (
        <Demo
          id={variant}
          outputWrapper={OutputWrapper}
          initialValues={{
            rotation: 0,
            axis: 'x',
          }}
          controls={[
            <DemoSliderControl
              key="rotation"
              id="rotation"
              label="Rotation"
              min={-45}
              max={45}
              previewRenderer={(val) => `${val}deg`}
            />,
            <DemoRadioControl
              key="axis"
              id="axis"
              label="Axis"
              options={[
                { label: 'X', value: 'x' },
                { label: 'Y', value: 'y' },
              ]}
            />,
          ]}
        >
          {({ rotation, axis }) => {
            return (
              <SkewDemo
                variant={variant}
                rotation={rotation}
                axis={axis}
              />
            );
          }}
        </Demo>
      );
    }

    case 'multiple-reversed':
    case 'multiple': {
      const controls = [
        <DemoSliderControl
          key="x"
          id="x"
          label="x"
          min={0}
          max={100}
          previewRenderer={(val) => `${val}px`}
        />,
        <DemoSliderControl
          key="rotation"
          id="rotation"
          label="Rotation"
          min={0}
          max={180}
          previewRenderer={(val) => `${val}deg`}
        />,
      ];

      if (variant === 'multiple-reversed') {
        controls.reverse();
      }

      return (
        <Demo
          id={variant}
          outputWrapper={OutputWrapper}
          initialValues={{
            x: 0,
            rotation: 0,
          }}
          controls={controls}
        >
          {({ x, rotation }) => {
            return (
              <MultipleDemo
                translateX={x}
                rotation={rotation}
                variant={variant}
              />
            );
          }}
        </Demo>
      );
    }

    case 'origin':
    case 'origin-scale': {
      return (
        <Demo
          id={variant}
          outputWrapper={OutputWrapper}
          initialValues={{
            rotation: 0,
            scale: 1,
            origin: 'center',
            showOriginIndicator: true,
          }}
          controls={[
            variant === 'origin' ? (
              <DemoSliderControl
                key="rotation"
                id="rotation"
                label="Rotation"
                min={0}
                max={360}
                previewRenderer={(val) => `${val}deg`}
              />
            ) : (
              <DemoSliderControl
                key="scale"
                id="scale"
                label="Scale"
                min={0}
                max={2}
                step={0.05}
              />
            ),
            <DemoSelectControl
              key="origin"
              id="origin"
              label="Transform Origin"
              options={[
                {
                  label: 'center (default)',
                  value: 'center',
                },
                'left top',
                '25px bottom',
                '0% 150%',
              ]}
            />,
            <DemoRadioControl
              key="showOriginIndicator"
              id="showOriginIndicator"
              label="Show Origin"
              options={[
                {
                  label: 'true',
                  value: true,
                },
                {
                  label: 'false',
                  value: false,
                },
              ]}
            />,
          ]}
          customGrid={{
            gridTemplateColumns: '1fr 1fr 1fr',
          }}
        >
          {({ rotation, scale, origin, showOriginIndicator }) => {
            return (
              <OriginDemo
                variant={variant}
                rotation={rotation}
                scale={scale}
                origin={origin}
                showOriginIndicator={showOriginIndicator}
              />
            );
          }}
        </Demo>
      );
    }

    case 'multiple-origin': {
      return (
        <Demo
          id={variant}
          outputWrapper={OutputWrapper}
          initialValues={{
            rotation: 0,
            x: 0,
            origin: 'center',
          }}
          controls={[
            <DemoSliderControl
              key="rotation"
              id="rotation"
              label="Rotation"
              min={0}
              max={360}
              previewRenderer={(val) => `${val}deg`}
            />,
            <DemoSliderControl
              key="x"
              id="x"
              label="x"
              min={0}
              max={100}
              previewRenderer={(val) => `${val}px`}
            />,
            <DemoSelectControl
              key="origin"
              id="origin"
              label="Transform Origin"
              options={[
                {
                  label: 'center (default)',
                  value: 'center',
                },
                'left top',
                '25px bottom',
                '0% 150%',
              ]}
            />,
          ]}
          customGrid={{
            gridTemplateColumns: '1fr 1fr 1fr',
          }}
        >
          {({ rotation, x, origin }) => {
            return (
              <MultipleOriginDemo
                variant={variant}
                rotation={rotation}
                x={x}
                origin={origin}
              />
            );
          }}
        </Demo>
      );
    }

    case 'enable-perspective': {
      return (
        <Demo
          id={variant}
          initialValues={{
            enablePerspective: 'false',
          }}
          controls={[
            <DemoRadioControl
              key="enablePerspective"
              id="enablePerspective"
              label="Enable Perspective"
              options={['true', 'false']}
            />,
          ]}
        >
          {({ enablePerspective }) => {
            return (
              <EnablePerspectiveDemo
                variant={variant}
                enablePerspective={enablePerspective}
              />
            );
          }}
        </Demo>
      );
    }

    case 'perspective': {
      return (
        <Demo
          id={variant}
          initialValues={{
            perspective: 250,
          }}
          controls={[
            <DemoSliderControl
              key="perspective"
              id="perspective"
              label="Perspective"
              min={50}
              max={600}
              previewRenderer={(val) => `${val}px`}
            />,
          ]}
        >
          {({ perspective }) => {
            return (
              <PerspectiveDemo
                variant={variant}
                perspective={perspective}
              />
            );
          }}
        </Demo>
      );
    }

    default: {
      return 'Unrecognized variant';
    }
  }
}

const OutputWrapper = styled(DemoOutputWrapper)`
  padding: 50px 32px 32px 32px;
`;

export default TransformDemo;
